// frontend/src/components/UI/PdfViewer.js
// Cross-platform PDF preview built on pdf.js (pdfjs-dist legacy build).
//
// WHY THIS EXISTS:
//   Native <iframe src={pdfBlob}> rendering does NOT work on Android
//   (Chrome / WebView ship no inline PDF engine), so HR on a phone saw a
//   blank panel when verifying a PDF medical certificate / NS order. pdf.js
//   rasterises each page to a <canvas> in pure JS, so it renders identically
//   on desktop, iOS, and Android.
//
// CONTROLS (floating overlay, 44px WCAG 2.5.8 touch targets):
//   zoom out · zoom in · rotate · fit-to-page · download
// INTERACTIONS:
//   Ctrl/⌘ + wheel → zoom   ·   plain wheel → scroll the document
//   drag/native scroll → pan when zoomed   ·   two-finger pinch → zoom (mobile)
//
// Pages re-render at the target scale (debounced) so small MC text stays crisp,
// with an interim CSS transform for smooth live zoom feedback.

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ZoomIn, ZoomOut, RotateCw, RefreshCw, Download, AlertTriangle } from 'lucide-react';
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';
import LoadingSpinner from './LoadingSpinner';

// Worker is copied to /public by scripts/copyPdfWorker.js (same-origin, no CDN —
// keeps confidential documents in-house and works offline in the PWA).
pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.js`;

const MIN_ZOOM = 0.5;
const MAX_ZOOM = 5;
const BUTTON_ZOOM_STEP = 0.25;
const WHEEL_ZOOM_IN_FACTOR = 1.1;
const WHEEL_ZOOM_OUT_FACTOR = 0.9;
const RENDER_DEBOUNCE_MS = 110;
const FIT_PADDING_PX = 24; // breathing room so the page never touches the panel edge

const clampZoom = (z) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z));

const getTouchDistance = (t1, t2) => {
  const dx = t1.clientX - t2.clientX;
  const dy = t1.clientY - t2.clientY;
  return Math.sqrt(dx * dx + dy * dy);
};

/**
 * @param {object}   props
 * @param {string}   props.src         Object URL (blob:) of the PDF to render.
 * @param {string}  [props.fileName]   Download filename (defaults to "document.pdf").
 * @param {function}[props.onDownload] Optional custom download handler. Falls back
 *                                     to an in-component blob download when omitted.
 * @param {string}  [props.title]      Accessible label / aria title.
 * @param {string}  [props.theme]      'admin' (red) | 'employee' (blue). Controls the
 *                                     toolbar button styling so it matches the host modal.
 */
const PdfViewer = ({ src, fileName = 'document.pdf', onDownload, title = 'PDF document', theme = 'admin' }) => {
  const toolbarBtnClass = theme === 'employee' ? 'icon-button' : 'icon-button-admin';
  const [pdfDoc, setPdfDoc] = useState(null);
  const [numPages, setNumPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [baseScale, setBaseScale] = useState(1);
  const [interimScale, setInterimScale] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);

  const containerRef = useRef(null);
  const wrapperRef = useRef(null);
  const canvasRefs = useRef([]);
  const firstPageRef = useRef(null);
  const renderedZoomRef = useRef(1);
  const renderTasksRef = useRef([]);
  const loadTokenRef = useRef(0);

  // Pinch state
  const lastPinchDistRef = useRef(null);

  // ── Load the PDF document ───────────────────────────────────────────────
  const loadPdf = useCallback(async () => {
    if (!src) return;
    const token = ++loadTokenRef.current;
    setLoading(true);
    setError(null);
    setPdfDoc(null);
    setNumPages(0);
    firstPageRef.current = null;

    try {
      const loadingTask = pdfjsLib.getDocument({ url: src });
      const pdf = await loadingTask.promise;
      if (token !== loadTokenRef.current) {
        pdf.destroy();
        return;
      }
      const firstPage = await pdf.getPage(1);
      if (token !== loadTokenRef.current) {
        pdf.destroy();
        return;
      }
      firstPageRef.current = firstPage;
      canvasRefs.current = new Array(pdf.numPages).fill(null);
      setPdfDoc(pdf);
      setNumPages(pdf.numPages);
      setZoom(1);
      setRotation(0);
      renderedZoomRef.current = 1;
    } catch (err) {
      if (token !== loadTokenRef.current) return;
      console.error('PdfViewer: failed to load PDF:', err);
      setError('Failed to load PDF preview');
    } finally {
      if (token === loadTokenRef.current) setLoading(false);
    }
  }, [src]);

  useEffect(() => {
    loadPdf();
    return () => {
      loadTokenRef.current++;
      renderTasksRef.current.forEach((t) => t && t.cancel && t.cancel());
      renderTasksRef.current = [];
    };
  }, [loadPdf]);

  // Destroy the pdf document when it changes / unmounts (frees worker memory).
  useEffect(() => {
    return () => {
      if (pdfDoc) {
        try { pdfDoc.destroy(); } catch { /* noop */ }
      }
    };
  }, [pdfDoc]);

  // ── Compute fit-to-width base scale ─────────────────────────────────────
  const computeBaseScale = useCallback(() => {
    const container = containerRef.current;
    const firstPage = firstPageRef.current;
    if (!container || !firstPage) return;
    const available = container.clientWidth - FIT_PADDING_PX;
    if (available <= 0) return;
    const unscaled = firstPage.getViewport({ scale: 1, rotation });
    const fit = available / unscaled.width;
    setBaseScale(fit > 0 ? fit : 1);
  }, [rotation]);

  // Recompute fit on load, rotation change, and container resize.
  useEffect(() => {
    if (!pdfDoc) return;
    computeBaseScale();
    const container = containerRef.current;
    if (!container || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => computeBaseScale());
    ro.observe(container);
    return () => ro.disconnect();
  }, [pdfDoc, computeBaseScale]);

  // ── Render all pages at the target scale (debounced for crispness) ──────
  useEffect(() => {
    if (!pdfDoc) return;
    let cancelled = false;

    // Interim visual feedback — instantly scale what's already rendered so the
    // zoom feels responsive, then snap back to 1 once the crisp re-render lands.
    setInterimScale(zoom / (renderedZoomRef.current || 1));

    const handle = setTimeout(async () => {
      const scale = baseScale * zoom;
      const dpr = window.devicePixelRatio || 1;

      // Cancel any in-flight renders from a previous scale/rotation.
      renderTasksRef.current.forEach((t) => t && t.cancel && t.cancel());
      renderTasksRef.current = [];

      for (let p = 1; p <= pdfDoc.numPages; p++) {
        if (cancelled) return;
        try {
          const page = await pdfDoc.getPage(p);
          if (cancelled) return;
          const viewport = page.getViewport({ scale, rotation });
          const canvas = canvasRefs.current[p - 1];
          if (!canvas) continue;
          const ctx = canvas.getContext('2d');
          canvas.width = Math.floor(viewport.width * dpr);
          canvas.height = Math.floor(viewport.height * dpr);
          canvas.style.width = `${Math.floor(viewport.width)}px`;
          canvas.style.height = `${Math.floor(viewport.height)}px`;
          const task = page.render({
            canvasContext: ctx,
            viewport,
            transform: dpr !== 1 ? [dpr, 0, 0, dpr, 0, 0] : null,
          });
          renderTasksRef.current[p - 1] = task;
          await task.promise;
        } catch (err) {
          if (err && err.name === 'RenderingCancelledException') return;
          console.error(`PdfViewer: failed to render page ${p}:`, err);
        }
      }

      if (!cancelled) {
        renderedZoomRef.current = zoom;
        setInterimScale(1);
      }
    }, RENDER_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [pdfDoc, zoom, rotation, baseScale, numPages]);

  // ── Zoom / rotate / fit handlers ────────────────────────────────────────
  const zoomIn = useCallback(() => setZoom((z) => clampZoom(z + BUTTON_ZOOM_STEP)), []);
  const zoomOut = useCallback(() => setZoom((z) => clampZoom(z - BUTTON_ZOOM_STEP)), []);
  const rotate = useCallback(() => setRotation((r) => (r + 90) % 360), []);
  const fit = useCallback(() => {
    setZoom(1);
    setRotation(0);
    if (containerRef.current) containerRef.current.scrollTo({ top: 0, left: 0 });
  }, []);

  const handleDownload = useCallback(() => {
    if (onDownload) {
      onDownload();
      return;
    }
    if (!src) return;
    const link = document.createElement('a');
    link.href = src;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [onDownload, src, fileName]);

  // ── Native wheel listener (Ctrl/⌘ + wheel → zoom; plain wheel → scroll) ──
  // Registered non-passively so preventDefault() actually suppresses the
  // browser's Ctrl+wheel page zoom. React's synthetic onWheel is passive.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const onWheel = (e) => {
      if (!(e.ctrlKey || e.metaKey)) return; // let plain scroll move the document
      e.preventDefault();
      const factor = e.deltaY > 0 ? WHEEL_ZOOM_OUT_FACTOR : WHEEL_ZOOM_IN_FACTOR;
      setZoom((z) => clampZoom(z * factor));
    };
    container.addEventListener('wheel', onWheel, { passive: false });
    return () => container.removeEventListener('wheel', onWheel);
  }, [pdfDoc]);

  // ── Pinch-to-zoom (mobile); single-finger falls through to native scroll ─
  const onTouchStart = useCallback((e) => {
    if (e.touches.length === 2) {
      lastPinchDistRef.current = getTouchDistance(e.touches[0], e.touches[1]);
    }
  }, []);

  const onTouchMove = useCallback((e) => {
    if (e.touches.length === 2) {
      e.preventDefault();
      const dist = getTouchDistance(e.touches[0], e.touches[1]);
      if (lastPinchDistRef.current) {
        const ratio = dist / lastPinchDistRef.current;
        setZoom((z) => clampZoom(z * ratio));
      }
      lastPinchDistRef.current = dist;
    }
  }, []);

  const onTouchEnd = useCallback((e) => {
    if (e.touches.length < 2) lastPinchDistRef.current = null;
  }, []);

  // Track which page is centred in the viewport (for the page indicator).
  const onScroll = useCallback(() => {
    if (numPages <= 1) return;
    const container = containerRef.current;
    if (!container) return;
    const mid = container.scrollTop + container.clientHeight / 2;
    let acc = 0;
    for (let i = 0; i < canvasRefs.current.length; i++) {
      const c = canvasRefs.current[i];
      if (!c) continue;
      acc += c.offsetHeight + 16; // canvas height + gap
      if (mid <= acc) {
        setCurrentPage(i + 1);
        return;
      }
    }
    setCurrentPage(numPages);
  }, [numPages]);

  // ── Render ──────────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-red-500" />
          <p className="text-red-600 font-medium mb-2">Failed to load document</p>
          <p className="text-sm text-gray-500 mb-4">{error}</p>
          <button onClick={loadPdf} className="btn btn-admin-outline">
            Retry Loading
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full h-full bg-gray-100 rounded-lg overflow-hidden">
      {/* Scroll container — plain scroll pans/scrolls; Ctrl+wheel & pinch zoom */}
      <div
        ref={containerRef}
        className="w-full h-full overflow-auto"
        style={{ WebkitOverflowScrolling: 'touch' }}
        onScroll={onScroll}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <div
          ref={wrapperRef}
          className="flex flex-col items-center gap-4 p-3"
          style={{
            minWidth: '100%',
            width: 'max-content',
            margin: '0 auto',
            transform: interimScale !== 1 ? `scale(${interimScale})` : undefined,
            transformOrigin: 'top center',
            transition: interimScale !== 1 ? 'none' : 'transform 0.1s ease-out',
          }}
        >
          {Array.from({ length: numPages }).map((_, i) => (
            <canvas
              key={i}
              ref={(el) => { canvasRefs.current[i] = el; }}
              className="bg-white shadow-md block max-w-none"
              aria-label={`${title} page ${i + 1}`}
            />
          ))}
        </div>
      </div>

      {/* Loading overlay */}
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100/80">
          <div className="text-center">
            <LoadingSpinner size="lg" />
            <p className="mt-2 text-gray-500">Loading document...</p>
          </div>
        </div>
      )}

      {/* Page indicator (multi-page only) */}
      {!loading && numPages > 1 && (
        <div className="absolute top-3 left-1/2 -translate-x-1/2 z-10 px-3 py-1 rounded-full bg-gray-900/75 text-white text-xs font-medium shadow-md pointer-events-none">
          Page {currentPage} / {numPages}
        </div>
      )}

      {/* Floating control bar — 44px touch targets (WCAG 2.5.8) */}
      {!loading && (
        <div
          className="absolute bottom-3 right-3 z-10 flex items-center gap-0.5 bg-white/95 border border-gray-200 rounded-full shadow-md p-1"
          onMouseDown={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            onClick={zoomOut}
            disabled={zoom <= MIN_ZOOM}
            className={`${toolbarBtnClass} disabled:opacity-40 disabled:cursor-not-allowed`}
            title="Zoom out"
            aria-label="Zoom out"
          >
            <ZoomOut className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={zoomIn}
            disabled={zoom >= MAX_ZOOM}
            className={`${toolbarBtnClass} disabled:opacity-40 disabled:cursor-not-allowed`}
            title="Zoom in"
            aria-label="Zoom in"
          >
            <ZoomIn className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={rotate}
            className={toolbarBtnClass}
            title="Rotate clockwise"
            aria-label="Rotate clockwise"
          >
            <RotateCw className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={fit}
            className={toolbarBtnClass}
            title="Fit to page"
            aria-label="Fit to page"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          <div className="w-px h-6 bg-gray-200 mx-0.5" />
          <button
            type="button"
            onClick={handleDownload}
            className={toolbarBtnClass}
            title="Download"
            aria-label="Download document"
          >
            <Download className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
};

export default PdfViewer;
